import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Globe } from "lucide-react";

import {
  REPLY_LANGUAGE_OPTIONS,
  getReplyLanguageShortLabel,
  type ReplyLanguageOverride,
} from "../lib/replyLanguage";

type ReplyLanguageSelectorProps = {
  value: ReplyLanguageOverride;
  onChange: (value: ReplyLanguageOverride) => Promise<void> | void;
  disabled?: boolean;
  compact?: boolean;
};

export default function ReplyLanguageSelector({
  value,
  onChange,
  disabled = false,
  compact = false,
}: ReplyLanguageSelectorProps) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const isActive = value !== "auto";

  const handleSelect = async (nextValue: ReplyLanguageOverride) => {
    if (saving || disabled || nextValue === value) {
      setOpen(false);
      return;
    }

    try {
      setSaving(true);
      await onChange(nextValue);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled || saving}
          title="Choose reply language"
          aria-label="Choose reply language"
          className={`shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border text-sm transition ${
            isActive
              ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
              : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
          } ${compact ? "h-10 w-10 px-0 py-0" : "px-3 py-2"} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <Globe className="w-4 h-4" />
          {!compact && (
            <span className="font-medium">{getReplyLanguageShortLabel(value)}</span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="end"
          sideOffset={8}
          className="z-[9999] w-48 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl animate-in fade-in slide-in-from-bottom-2"
        >
          <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Reply language
          </p>
          <ul className="space-y-1">
            {REPLY_LANGUAGE_OPTIONS.map((option) => {
              const selected = option.value === value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => void handleSelect(option.value)}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition ${
                      selected
                        ? "bg-blue-50 text-blue-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span>{option.label}</span>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {option.shortLabel}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
